import { forwardRef, useState } from 'react'

import styles from './CouponInput.module.css'
import { CheckIcon } from '../../icons'

export interface CouponInputProps {
  /** Placeholder text */
  placeholder?: string
  /** Apply button text */
  applyText?: string
  /** Callback when apply is clicked */
  onApply: (code: string) => void
  /** Whether the apply action is loading */
  isLoading?: boolean
  /** Whether coupon was successfully applied */
  isSuccess?: boolean
  /** Error message to display */
  error?: string
  /** Additional class name */
  className?: string
}

/**
 * CouponInput - Input field with Apply button for coupon codes
 * Design from Figma: input 346x40px + button 110x40px
 */
export const CouponInput = forwardRef<HTMLDivElement, CouponInputProps>(
  (
    {
      placeholder = 'Enter coupon code',
      applyText = 'Apply',
      onApply,
      isLoading = false,
      isSuccess = false,
      error,
      className,
    },
    ref
  ) => {
    const [value, setValue] = useState('')

    const handleApply = () => {
      if (value.trim()) {
        onApply(value.trim())
      }
    }

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Enter' && value.trim()) {
        handleApply()
      }
    }

    const hasInput = value.trim().length > 0
    const isDisabled = !hasInput || isLoading

    const buttonClasses = [
      styles.button,
      hasInput ? styles.hasInput : '',
      isLoading ? styles.buttonLoading : '',
      isSuccess ? styles.buttonSuccess : '',
      isDisabled && !isLoading ? styles.buttonDisabled : '',
    ]
      .filter(Boolean)
      .join(' ')

    return (
      <div ref={ref} className={`${styles.wrapper} ${className ?? ''}`}>
        <div className={styles.inputGroup}>
          <input
            type="text"
            className={styles.input}
            placeholder={placeholder}
            value={value}
            onChange={(e) => setValue(e.target.value)}
            onKeyDown={handleKeyDown}
            disabled={isLoading || isSuccess}
            data-ga-module="coupon"
          />
          <button
            type="button"
            className={buttonClasses}
            onClick={handleApply}
            disabled={isDisabled || isSuccess}
            data-ga-action-name="coupon_apply_click"
            data-ga-module="coupon"
          >
            {isLoading ? (
              <span className={styles.spinner} />
            ) : isSuccess ? (
              <CheckIcon className={styles.successIcon} />
            ) : (
              <span>{applyText}</span>
            )}
          </button>
        </div>
        {error && <p className={styles.error}>{error}</p>}
      </div>
    )
  }
)

CouponInput.displayName = 'CouponInput'
